import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

const categoryDescriptions: Record<string, string> = {
  love: '甜蜜浪漫的恋爱韩漫，从校园到职场，从初恋到婚姻，这里有最动人的爱情故事。',
  suspense: '紧张刺激的悬疑推理韩漫，烧脑剧情让你欲罢不能。真相永远只有一个！',
  urban: '真实温暖的都市生活韩漫，讲述在繁华都市中每一个普通人的故事。',
  fantasy: '宏大奇幻的冒险韩漫，魔法、龙、异世界，让你的想象力尽情飞翔。',
  action: '热血沸腾的动作韩漫，极限战斗、速度与激情，每一页都让你肾上腺素飙升。',
  comedy: '爆笑不断的搞笑韩漫，轻松愉快的日常喜剧，每天为你带来好心情。',
  horror: '毛骨悚然的恐怖韩漫，午夜惊魂、灵异事件，胆小的读者请慎重观看。',
  history: '气势恢宏的历史韩漫，穿越时空感受古代朝鲜的风云变幻。',
};

const categoryHeroImages: Record<string, string> = {
  love: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20romance%20soft%20pink%20atmosphere%20cherry%20blossoms%20gentle%20illustration%20background%20clean%20minimal%20warm%20lighting%20dreamy%20mood&width=1920&height=600&seq=cat-hero-love&orientation=landscape',
  suspense: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20mystery%20thriller%20dark%20teal%20atmosphere%20foggy%20alley%20dramatic%20lighting%20suspenseful%20mood%20clean%20cinematic%20background&width=1920&height=600&seq=cat-hero-suspense&orientation=landscape',
  urban: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20modern%20city%20night%20scene%20warm%20apartment%20windows%20neutral%20gray%20beige%20atmosphere%20calm%20urban%20mood%20clean%20background&width=1920&height=600&seq=cat-hero-urban&orientation=landscape',
  fantasy: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20fantasy%20epic%20landscape%20dragon%20castle%20golden%20amber%20warm%20light%20magical%20atmosphere%20clean%20cinematic%20background&width=1920&height=600&seq=cat-hero-fantasy&orientation=landscape',
  action: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20action%20dynamic%20red%20dark%20atmosphere%20energy%20explosion%20powerful%20mood%20clean%20cinematic%20background%20intense&width=1920&height=600&seq=cat-hero-action&orientation=landscape',
  comedy: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20comedy%20bright%20yellow%20orange%20playful%20atmosphere%20fun%20vibrant%20mood%20clean%20cartoon%20style%20background&width=1920&height=600&seq=cat-hero-comedy&orientation=landscape',
  horror: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20horror%20dark%20green%20eerie%20atmosphere%20abandoned%20corridor%20spooky%20mood%20clean%20cinematic%20background&width=1920&height=600&seq=cat-hero-horror&orientation=landscape',
  history: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20historical%20ancient%20palace%20warm%20earth%20gold%20traditional%20atmosphere%20elegant%20mood%20clean%20cinematic%20background&width=1920&height=600&seq=cat-hero-history&orientation=landscape',
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.category === slug);
  const description = categoryDescriptions[slug || ''] || '探索更多精彩的韩漫作品。';
  const heroImage = categoryHeroImages[slug || ''] || categoryHeroImages.fantasy;

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h1>
            <p className="text-foreground-500 mb-4">请检查分类地址是否正确</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const canonicalUrl = `http://vktest.com/category/${slug}`;

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
          <img
            src={heroImage}
            alt={`${category.name}韩漫`}
            title={`${category.name}韩国漫画 - 韩漫漫画网`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/40 via-foreground-900/20 to-foreground-900/50"></div>
          <div className="relative z-10 w-full h-full flex items-center">
            <div className="w-full px-4 md:px-6">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-2">
                {category.name}
              </h1>
              <p className="text-background-50/80 text-sm md:text-base max-w-xl">{description}</p>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: `${category.name}漫画` },
              ]}
            />

            <div className="flex items-center justify-between mb-6">
              <p className="text-foreground-500 text-sm">
                共 <strong className="text-foreground-900">{categoryComics.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序：</span>
                <select className="text-sm bg-background-100 border border-background-300 rounded-full px-3 py-1.5 text-foreground-700 outline-none cursor-pointer">
                  <option>最新更新</option>
                  <option>评分最高</option>
                  <option>人气最热</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {categoryComics.map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  slug={comic.slug}
                  title={comic.title}
                  coverImage={comic.coverImage}
                  rating={comic.rating}
                  latestChapter={comic.latestChapter}
                  status={comic.status}
                />
              ))}
            </div>

            {categoryComics.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无漫画，敬请期待。</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${category.name}韩漫 - 韩漫漫画网`,
          "description": description,
          "url": canonicalUrl,
          "isPartOf": {
            "@type": "WebSite",
            "name": "韩漫漫画网",
            "url": "http://vktest.com/"
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://vktest.com/" },
              { "@type": "ListItem", "position": 2, "name": `${category.name}漫画`, "item": canonicalUrl }
            ]
          }
        })
      }} />

      <Footer />
    </>
  );
}